import { useCallback, useEffect, useState } from 'react';
import type { SlackChannelPurpose, SlackChannelBinding } from '@reef/schema';
import { api, type SlackChannelSummary, type SlackIdentity, type WorkflowSummary } from '../api.ts';

/**
 * Where reef posts in Slack, for the whole deployment.
 *
 * `/reef connect` in a channel is the path most people take, and it is better:
 * you can take it while looking at the channel you mean. This exists for the
 * two things it cannot do.
 *
 * **Seeing all of it.** A channel you were never invited to is invisible from
 * inside Slack, so without this there is no way to answer "what is reef posting
 * to, and who asked for that" — which is a question about the deployment's
 * blast radius, since a workspace-wide binding carries every plan from every
 * workflow.
 *
 * **Taking one away.** `/reef disconnect` requires standing in the channel, so
 * a binding made by somebody who has since left is otherwise permanent.
 *
 * The diagnostic leads, and that is not decoration. The most common Slack
 * failure is a missing scope, and it is silent: `users.conversations` answers
 * `missing_scope`, reef renders an empty picker, and it reads as a workspace
 * with no channels. Saying which of the two it is turns "Slack is broken" into
 * one line in the app's OAuth settings.
 */
export function SlackSettings({
  isAdmin,
  workflows,
}: {
  isAdmin: boolean;
  workflows: WorkflowSummary[];
}) {
  const [bindings, setBindings] = useState<SlackChannelBinding[]>([]);
  const [configured, setConfigured] = useState(true);
  const [channels, setChannels] = useState<SlackChannelSummary[]>([]);
  const [channelReason, setChannelReason] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [draftChannel, setDraftChannel] = useState('');
  const [draftWorkflow, setDraftWorkflow] = useState('');
  const [draftPurpose, setDraftPurpose] = useState<SlackChannelPurpose>('all');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const settings = await api.slackSettings();
      setBindings(settings.bindings);
      setConfigured(settings.configured);

      const listed = await api.listSlackBotChannels();
      setChannels(listed.channels);
      setChannelReason(listed.reason ?? null);
      setError(null);
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    /*
     * A non-admin is not offered "every workflow", so an empty draft would be
     * a Connect button that always 403s. Defaulted to the first workflow they
     * can see instead — the choice they are allowed to make.
     */
    if (!isAdmin && !draftWorkflow && workflows.length > 0) setDraftWorkflow(workflows[0]!.id);
  }, [isAdmin, draftWorkflow, workflows]);

  const bind = async () => {
    if (!draftChannel) return;
    setBusy('bind');
    setError(null);

    try {
      const channel = channels.find((entry) => entry.id === draftChannel);
      await api.bindSlackChannel({
        channelId: draftChannel,
        ...(channel ? { channelName: channel.name } : {}),
        ...(draftWorkflow ? { workflowId: draftWorkflow } : {}),
        purpose: draftPurpose,
      });
      setNote(
        draftWorkflow
          ? `Reef will post ${draftWorkflow} here.`
          : 'Reef will post every workflow here.',
      );
      setDraftChannel('');
      await refresh();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const unbind = async (binding: SlackChannelBinding) => {
    setBusy(binding.id);
    setError(null);

    try {
      await api.unbindSlackChannel(binding.channelId, binding.workflowId);
      setNote('Removed. Reef will stop posting there.');
      await refresh();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <section className="studio-card">
        <div className="studio-card-head">
          <h2>Slack</h2>
          {/*
            Neutral for the normal state, `warn` only for the broken one. A
            green "connected" badge beside every other neutral chip in the head
            row would read as a badge rather than as the same object in a
            different state — the reason `.chip.warn` is border-and-text too.
          */}
          <span className={configured ? 'chip' : 'chip warn'}>
            {configured ? 'connected' : 'not configured'}
          </span>
          <span className="spacer" />
          <button className="ghost" onClick={() => void refresh()} disabled={loading}>
            Refresh
          </button>
        </div>

        <div className="studio-card-body">
          {error && <div className="banner danger">{error}</div>}
          {note && !error && <div className="banner">{note}</div>}

          {!configured && (
            /*
             * Two secrets, named, because the failure modes are different and a
             * generic "Slack is not set up" sends people to the wrong one. They
             * are `wrangler secret put` values rather than rows in the secret
             * store, so this says where they live rather than offering a field
             * that would imply reef can read them back.
             */
            <div className="banner warn">
              <h3>No Slack on this deployment</h3>
              <p style={{ margin: 0 }}>
                Set <code>SLACK_SIGNING_SECRET</code> and <code>SLACK_BOT_TOKEN</code> with{' '}
                <code>wrangler secret put</code>. Without the first, all three Slack endpoints
                answer 503; without the second, reef records approvals and posts nothing — the
                review queue still empties them.
              </p>
            </div>
          )}

          {configured && channelReason && (
            <div className="banner warn">
              <h3>The bot can see no channels</h3>
              <p style={{ margin: 0 }}>
                Slack said <code>{channelReason}</code>. If that is a missing scope, add{' '}
                <code>channels:read</code> and <code>groups:read</code> under OAuth &amp;
                Permissions and reinstall the app. Until then reviews can still be posted to a
                channel id you know, but nothing here can list one for you.
              </p>
            </div>
          )}

          {configured && !channelReason && (
            <p className="hint" style={{ marginTop: 0 }}>
              The bot is in {channels.length} channel{channels.length === 1 ? '' : 's'}. A review
              can only be posted to a channel it has been invited to — <code>/invite @reef</code>.
            </p>
          )}
        </div>
      </section>

      <section className="studio-card">
        <div className="studio-card-head">
          <h2>Connected channels</h2>
          {bindings.length > 0 && <span className="chip">{bindings.length}</span>}
        </div>

        <div className="studio-card-body">
          {bindings.length === 0 ? (
            <p className="hint" style={{ marginTop: 0 }}>
              Nothing is connected. Reviews go wherever a step's <code>approvalChannel</code> names,
              and nowhere else. Anyone can connect the channel they are standing in with{' '}
              <code>/reef connect</code>.
            </p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Channel</th>
                  <th>Workflow</th>
                  <th>Posts</th>
                  <th>Connected by</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {bindings.map((binding) => (
                  <tr key={binding.id}>
                    <td>
                      <code>{binding.channelName ? `#${binding.channelName}` : binding.channelId}</code>
                    </td>
                    <td>
                      {binding.workflowId ? (
                        <code>{binding.workflowId}</code>
                      ) : (
                        /*
                         * Called out rather than shown as a blank cell. A
                         * workspace-wide binding is the one with real blast
                         * radius — every plan from every workflow, including
                         * ones the reader cannot open.
                         */
                        <span className="chip warn">every workflow</span>
                      )}
                    </td>
                    <td>{describePurpose(binding.purpose)}</td>
                    <td className="hint">
                      {binding.createdBy} · {new Date(binding.createdAt).toLocaleDateString()}
                    </td>
                    <td>
                      <button
                        className="ghost"
                        onClick={() => void unbind(binding)}
                        disabled={busy !== null}
                        title={
                          binding.workflowId
                            ? `Needs edit on ${binding.workflowId}`
                            : 'Needs the reef-admins group'
                        }
                      >
                        {busy === binding.id ? 'Removing…' : 'Remove'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <LinkedAccounts isAdmin={isAdmin} />

      <section className="studio-card">
        <div className="studio-card-head">
          <h2>Connect a channel</h2>
        </div>

        <div className="studio-card-body">
          <p className="hint" style={{ marginTop: 0 }}>
            For a channel you are not in. From inside Slack, <code>/reef connect</code> is quicker
            and does the same thing.
          </p>

          <div className="studio-row">
            <div className="field">
              <label htmlFor="slack-bind-channel">Channel</label>
              <select
                id="slack-bind-channel"
                value={draftChannel}
                onChange={(event) => setDraftChannel(event.target.value)}
                disabled={channels.length === 0}
              >
                <option value="">choose a channel</option>
                {channels.map((channel) => (
                  <option key={channel.id} value={channel.id}>
                    {channel.private ? '🔒 ' : '# '}
                    {channel.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="slack-bind-workflow">Workflow</label>
              <select
                id="slack-bind-workflow"
                value={draftWorkflow}
                onChange={(event) => setDraftWorkflow(event.target.value)}
              >
                {/*
                  Every workflow needs an admin, so it is offered only to one —
                  a choice that always produces a 403 is worse than an absent
                  one, and this is the choice with the blast radius.
                */}
                {isAdmin && <option value="">every workflow</option>}
                {workflows.map((workflow) => (
                  <option key={workflow.id} value={workflow.id}>
                    {workflow.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="slack-bind-purpose">Posts</label>
              <select
                id="slack-bind-purpose"
                value={draftPurpose}
                onChange={(event) => setDraftPurpose(event.target.value as SlackChannelPurpose)}
              >
                <option value="all">reviews and runs</option>
                <option value="approvals">reviews only</option>
                <option value="runs">runs only</option>
              </select>
            </div>
          </div>

          {/*
            The audiences are different, and saying so is what stops a channel
            being muted: a reviewer wants only what is waiting on them, an
            operator wants to watch runs, and giving both to either is noise.
          */}
          <p className="hint">
            <strong>Reviews</strong> are what has stopped and needs a person.{' '}
            <strong>Runs</strong> are one threaded message per run — it starts, it finishes, and
            everything in between replies to itself.
          </p>

          <div className="studio-row">
            <button
              onClick={() => void bind()}
              disabled={!draftChannel || (!isAdmin && !draftWorkflow) || busy !== null}
            >
              {busy === 'bind' ? 'Connecting…' : 'Connect'}
            </button>
            {!isAdmin && (
              <span className="hint">
                A channel bound to every workflow receives every plan, including from workflows you
                cannot open — so that one needs the <code>reef-admins</code> group. Naming a
                workflow needs <code>edit</code> on it.
              </span>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

/**
 * Which Slack accounts reef has been told belong to which people.
 *
 * Linking is how a Slack click becomes somebody's reef access: the bot DMs a
 * one-time button, the person completes the normal Okta login, and only then is
 * the pair recorded. So this list is a list of standing credentials — and until
 * now it could only be added to. `unlink` has been on the store since the
 * identity model was written and was reachable from nothing, which left two
 * situations with no remedy at all:
 *
 * - somebody who linked the wrong Slack account, since the row is keyed by the
 *   Slack id and linking again does not replace it;
 * - an offboarded user, whose mapping outlives them. Access checks resolve
 *   group membership live, so it is not an escalation — but it is stale state
 *   nobody could see, let alone clear.
 *
 * Unlinking only ever removes access: the Slack user stops resolving to a reef
 * user and is offered a fresh link button on their next message. It fails
 * closed, which is why there is no confirmation beyond the click.
 */
function LinkedAccounts({ isAdmin }: { isAdmin: boolean }) {
  const [identities, setIdentities] = useState<SlackIdentity[]>([]);
  const [hidden, setHidden] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const result = await api.listSlackIdentities();
      setIdentities(result.identities);
      setHidden(result.hidden);
      setError(null);
    } catch (caught) {
      setError((caught as Error).message);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const unlink = async (identity: SlackIdentity) => {
    setBusy(identity.externalId);
    setError(null);
    try {
      await api.unlinkSlackIdentity(identity.externalId);
      setNote(
        identity.isSelf
          ? 'Unlinked. Reef will offer you a fresh link button the next time you message it.'
          : `Unlinked. That Slack account no longer acts as ${identity.label}.`,
      );
      await refresh();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="studio-card">
      <div className="studio-card-head">
        <h2>Linked accounts</h2>
        {identities.length > 0 && <span className="chip">{identities.length}</span>}
      </div>

      <div className="studio-card-body">
        {error && <div className="banner danger">{error}</div>}
        {note && !error && <div className="banner">{note}</div>}

        {identities.length === 0 ? (
          <p className="hint" style={{ marginTop: 0 }}>
            {isAdmin
              ? 'Nobody has linked a Slack account yet. The first time somebody messages the bot it DMs them a link button.'
              : 'You have not linked a Slack account. Message the bot in Slack once — it sends a link button, and you sign in with Okta as usual.'}
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Reef user</th>
                <th>Slack id</th>
                <th>Linked</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {identities.map((identity) => (
                <tr key={identity.externalId}>
                  <td>
                    {identity.label}
                    {identity.isSelf && <span className="pill accent">you</span>}
                  </td>
                  <td>
                    <code>{identity.externalId}</code>
                  </td>
                  <td className="hint">{new Date(identity.linkedAt).toLocaleDateString()}</td>
                  <td>
                    <button
                      className="ghost"
                      onClick={() => void unlink(identity)}
                      disabled={busy !== null}
                    >
                      {busy === identity.externalId ? 'Unlinking…' : 'Unlink'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <p className="hint">
          {isAdmin
            ? 'Unlinking is the offboarding step: the Slack account stops resolving to that reef user, so it can no longer approve, start or stop anything as them.'
            : 'You can unlink your own. An admin can unlink anybody’s, which is how an account is retired.'}
          {hidden > 0 && ` ${hidden} other link${hidden === 1 ? '' : 's'} exist that only an admin can see.`}
        </p>
      </div>
    </section>
  );
}

function describePurpose(purpose: SlackChannelPurpose): string {
  if (purpose === 'approvals') return 'reviews';
  if (purpose === 'runs') return 'runs';
  return 'reviews and runs';
}
